"use client";

import { sans } from "@/app/utils/fonts";
import { motion } from "motion/react";
import Image from "next/image";
import { Fragment } from "react";
import { useSectionView } from "@/app/utils/analytics";

const STEPS = [
  {
    n: 1,
    img: "/images/general/github-logo.webp",
    alt: "GitHub logo",
    title: "Get access",
    body: "Purchase ExtensionFast and log in for instant access to the repo.",
  },
  {
    n: 2,
    img: "/images/general/gear-icon.webp",
    alt: "Gear icon",
    title: "Set up once",
    body: "Point your AI agent at it, or follow the docs. You only do this once.",
  },
  {
    n: 3,
    img: "/images/general/upload-icon.webp",
    alt: "Upload icon",
    title: "Ship & earn",
    body: "Publish to Chrome, Firefox & Edge and start collecting payments.",
  },
];

export default function HorizontalSteps() {
  const sectionRef = useSectionView("how_it_works");
  return (
    <section
      ref={sectionRef}
      className="relative w-full overflow-hidden bg-ink"
      id="works"
    >
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="bg-dot-grid absolute inset-0 [mask-image:radial-gradient(700px_circle_at_50%_30%,black,transparent)]" />
        <div className="absolute left-1/2 top-8 h-72 w-[700px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(11,99,222,0.1),transparent_70%)] blur-2xl" />
      </div>

      <div className="mx-auto flex max-w-6xl flex-col items-center px-6 py-24">
        {/* badge */}
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-line bg-panel/70 px-4 py-1.5 text-sm font-medium text-zinc-300 shadow-sm backdrop-blur">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-primary"
          >
            <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
            <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
            <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
            <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
          </svg>
          Launch in 3 steps
        </div>

        <h2
          className={`${sans.className} max-w-full text-center text-4xl font-medium tracking-tight text-white md:text-5xl`}
        >
          From idea to{" "}
          <span className="text-primary">published extension</span> in 3 steps
        </h2>

        <motion.div className="mt-14 flex flex-col items-center gap-10 lg:flex-row lg:items-start lg:gap-2">
          {STEPS.map((s, i) => (
            <Fragment key={s.n}>
              <div className="flex w-[250px] flex-col items-center gap-6 text-center">
                <div className="relative flex h-20 w-20 items-center justify-center rounded-2xl bg-primary p-5 shadow-[0_10px_36px_-8px_rgba(11,99,222,0.6)] ring-1 ring-inset ring-white/15">
                  <Image
                    className="w-full"
                    src={s.img}
                    width={100}
                    height={100}
                    alt={s.alt}
                  />
                  <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-line bg-panel text-xs font-bold text-primary shadow-sm">
                    {s.n}
                  </span>
                </div>
                <div>
                  <p
                    className={`${sans.className} text-xl font-semibold text-white`}
                  >
                    {s.title}
                  </p>
                  <p className="mt-2 text-zinc-400">{s.body}</p>
                </div>
              </div>
              {i < STEPS.length - 1 && (
                <div className="mt-10 hidden h-px w-20 border-t-2 border-dashed border-line lg:block xl:w-32" />
              )}
            </Fragment>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
